import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";
import TimeUnit from "../../../../shared/units/TimeUnit";
import {InputSwitch} from "primereact/inputswitch";
import {Slider} from "primereact/slider";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import TransitionDialogFields from "../../field/page/TransitionDialogFields";
import DocumentSettings from "../../../document/DocumentSettings";
import PageSelection, {PAGERANGE_ALL} from "../../../document/PageSelection";
import MimeType from "../../../document/mimetype/MimeType";
import PageRange from "../../../../shared/components/PageRange";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AddPageTransitions,
	AdminWebserviceStatus,
	BaseToolbox,
	Functions,
	Information,
	RemovePageTransitions,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	Transition,
	TransitionDirection,
	TransitionsDocument,
	TransitionStyle
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {PortalToolboxOptionsOptions} from "../../../../shared/models/PortalToolboxOptionsOptions";
import {
	PageTransitionsFunction,
	PortalPageTransitionsToolboxOptions
} from "../../../../shared/models/PortalPageTransitionsToolboxOptions";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Info from "../../../../shared/components/Info";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import DateHelper from "../../../../shared/helper/DateHelper";
import MessageBox from "../../../../shared/components/MessageBox";

interface TransitionDialogStatus extends PortalDialogState<PortalToolboxOptionsOptions> {
	pages: number;
	currentPage: string;
	hasTransition: boolean;
}

export class TransitionDialogDefinition extends PortalDialog<PortalToolboxOptionsOptions,
	PortalDialogProperties<PortalToolboxOptionsOptions>, TransitionDialogStatus> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			pages: 1,
			currentPage: "1",
			hasTransition: false,
			fieldErrors: new Map<TransitionDialogFields, string>()
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.editTransitions, Functions.getEditTransitionsDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.TRANSITION, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderContents(): ReactNode {
		let parameter: PortalPageTransitionsToolboxOptions | undefined =
			this.state.parameter?.data?.pageTransitions as PortalPageTransitionsToolboxOptions;
		let errors: Map<TransitionDialogFields, string> = this.state.fieldErrors;
		let addParameter: AddPageTransitions | undefined = parameter?.add![0];

		if (!FunctionHelper.exists(parameter) || !FunctionHelper.exists(addParameter)) {
			return null;
		}

		return (
			<>
				{
					FunctionHelper.applyValue(
						addParameter?.pageTransition?.duration, Transition.getDurationDefault()
					) > 10 &&
					<div className="p-col-12">
						<MessageBox {...this.props} type={MessageType.INFO} closeable={true}>
							<Translate {...this.props} data={"transition.duration_info"} namespace={"dialog"}/>
						</MessageBox>
					</div>
				}
				<fieldset className="p-col-12 p-grid" hidden={!this.state.hasTransition}>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(TransitionDialogFields.MODE)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-mode">
							<Translate {...this.props} data="transition.mode.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-mode"
									  value={FunctionHelper.applyValue(
										  parameter.mode, PortalPageTransitionsToolboxOptions.getModeDefault()
									  )}
									  options={[
										  {
											  label: Translate.value("transition.mode.add", {ns: "dialog"}),
											  value: PageTransitionsFunction.ADD
										  },
										  {
											  label: Translate.value(
												  "transition.mode.remove", {ns: "dialog"}
											  ),
											  value: PageTransitionsFunction.REMOVE
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  TransitionDialogFields.MODE, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={parameter.mode === PageTransitionsFunction.REMOVE}>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(TransitionDialogFields.STYLE)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-style">
							<Translate {...this.props} data="transition.settings.style.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-style"
									  value={FunctionHelper.applyValue(
										  addParameter.pageTransition?.style, Transition.getStyleDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "transition.settings.style.blinds", {ns: "dialog"}
											  ),
											  value: TransitionStyle.Blinds
										  },
										  {
											  label: Translate.value(
												  "transition.settings.style.box", {ns: "dialog"}
											  ),
											  value: TransitionStyle.Box
										  },
										  {
											  label: Translate.value(
												  "transition.settings.style.cover", {ns: "dialog"}
											  ),
											  value: TransitionStyle.Cover
										  },
										  {
											  label: Translate.value(
												  "transition.settings.style.dissolve", {ns: "dialog"}
											  ),
											  value: TransitionStyle.Dissolve
										  },
										  {
											  label: Translate.value(
												  "transition.settings.style.fade", {ns: "dialog"}
											  ),
											  value: TransitionStyle.Fade
										  },
										  {
											  label: Translate.value(
												  "transition.settings.style.fly", {ns: "dialog"}
											  ),
											  value: TransitionStyle.Fly
										  },
										  {
											  label: Translate.value(
												  "transition.settings.style.glitter", {ns: "dialog"}
											  ),
											  value: TransitionStyle.Glitter
										  },
										  {
											  label: Translate.value(
												  "transition.settings.style.push", {ns: "dialog"}
											  ),
											  value: TransitionStyle.Push
										  },
										  {
											  label: Translate.value(
												  "transition.settings.style.split", {ns: "dialog"}
											  ),
											  value: TransitionStyle.Split
										  },
										  {
											  label: Translate.value(
												  "transition.settings.style.uncover", {ns: "dialog"}
											  ),
											  value: TransitionStyle.Uncover
										  },
										  {
											  label: Translate.value(
												  "transition.settings.style.wipe", {ns: "dialog"}
											  ),
											  value: TransitionStyle.Wipe
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  TransitionDialogFields.STYLE, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(TransitionDialogFields.DIRECTION)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-direction">
							<Translate {...this.props} data="transition.settings.direction.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-direction"
									  value={FunctionHelper.applyValue(
										  addParameter.pageTransition?.direction, Transition.getDirectionDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "transition.settings.direction.left_to_right",
												  {ns: "dialog"}
											  ),
											  value: TransitionDirection.LeftToRight
										  },
										  {
											  label: Translate.value(
												  "transition.settings.direction.bottom_to_top",
												  {ns: "dialog"}
											  ),
											  value: TransitionDirection.BottomToTop
										  },
										  {
											  label: Translate.value(
												  "transition.settings.direction.right_to_left",
												  {ns: "dialog"}
											  ),
											  value: TransitionDirection.RightToLeft
										  },
										  {
											  label: Translate.value(
												  "transition.settings.direction.top_to_bottom",
												  {ns: "dialog"}
											  ),
											  value: TransitionDirection.TopToBottom
										  },
										  {
											  label: Translate.value(
												  "transition.settings.direction.top_left_to_bottom_right",
												  {ns: "dialog"}
											  ),
											  value: TransitionDirection.TopLeftToBottomRight
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  TransitionDialogFields.DIRECTION, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(TransitionDialogFields.DURATION)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-duration">
							<Translate {...this.props} data="transition.settings.duration" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7"
							 hidden={!this.state.fields?.contains(TransitionDialogFields.DURATION)}>
							<InputNumber inputId="control-duration" step={1} suffix={" " + TimeUnit.MILLISECOND}
										 showButtons={true} useGrouping={false}
										 value={DateHelper.secondsToUnit(FunctionHelper.applyValue(
											 addParameter?.pageTransition?.duration, Transition.getDurationDefault()
										 ), TimeUnit.MILLISECOND)}
										 min={Transition.getDurationMin()}
										 onChange={(e) => this.onChange(
											 TransitionDialogFields.DURATION, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(TransitionDialogFields.AUTO_SCROLL)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-auto"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-auto"
										 checked={FunctionHelper.applyValue(
											 addParameter.pageTransition?.autoScroll,
											 Transition.getAutoScrollDefault()
										 )}
										 onChange={(e) => this.onChange(
											 TransitionDialogFields.AUTO_SCROLL, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-auto">
								<Translate {...this.props} data="transition.settings.auto_scroll.label"
										   namespace="dialog"/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 !this.state.fields?.contains(TransitionDialogFields.SCROLL_TIME) ||
							 !addParameter.pageTransition?.autoScroll
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-scroll-time">
							<Translate {...this.props} data="transition.settings.auto_scroll.time" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7"
							 hidden={!this.state.fields?.contains(TransitionDialogFields.SCROLL_TIME)}>
							<InputNumber inputId="control-scroll-time" suffix={" " + TimeUnit.MILLISECOND}
										 showButtons={true} step={1} useGrouping={false}
										 value={DateHelper.secondsToUnit(FunctionHelper.applyValue(
											 addParameter?.pageTransition?.autoScrollTime,
											 Transition.getAutoScrollTimeDefault()
										 ), TimeUnit.MILLISECOND)}
										 min={Transition.getAutoScrollTimeMin()}
										 onChange={(e) => this.onChange(
											 TransitionDialogFields.SCROLL_TIME, e.value
										 )}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={
					parameter.mode === PageTransitionsFunction.REMOVE ||
					addParameter.pageTransition?.style !== TransitionStyle.Fly
				}>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(TransitionDialogFields.FLY_OPAQUE)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-opaque"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-opaque"
										 checked={FunctionHelper.applyValue(
											 addParameter.pageTransition?.flyAreaOpaque,
											 Transition.getFlyAreaOpaqueDefault()
										 )}
										 onChange={(e) => this.onChange(
											 TransitionDialogFields.FLY_OPAQUE, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-opaque">
								<Translate {...this.props} data="transition.fly.opaque.label" namespace="dialog"/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-5" htmlFor="control-scale">
							<Translate {...this.props} data="transition.fly.scale" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 slider-input">
							<InputNumber inputId="control-scale" step={1} suffix=" %" showButtons={true}
										 value={FunctionHelper.applyValue(
											 addParameter.pageTransition?.flyScale, Transition.getFlyScaleDefault()
										 )}
										 min={1} max={1000} useGrouping={false}
										 onValueChange={(e) => this.onChange(
											 TransitionDialogFields.FLY_SCALE, e.value
										 )}/>
							<Slider min={1} max={1000} step={1}
									value={FunctionHelper.applyValue(
										addParameter.pageTransition?.flyScale, Transition.getFlyScaleDefault()
									)}
									onChange={(e) => this.onChange(
										TransitionDialogFields.FLY_SCALE, e.value as number
									)}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid"
						  hidden={!this.state.fields?.contains(TransitionDialogFields.PAGERANGE)}>
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-5" htmlFor="control-pagerange">
							<Translate {...this.props} data="transition.pages.label" namespace="dialog"/>
							<Info {...this.props} data="transition.pages.info" options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<PageRange inputId="control-pagerange"
									   className={errors.has(TransitionDialogFields.PAGERANGE) ? " p-invalid" : ""}
									   value={FunctionHelper.applyValue(
										   addParameter.pageTransition?.pages, Transition.getPagesDefault()
									   )}
									   onChange={(e) => this.onChange(
										   TransitionDialogFields.PAGERANGE, e.value
									   )}
									   appendTo={document.getElementById("root")}
									   addon={Translate.value(
										   "inputs.pagerange.of", {pages: this.state.pages}
									   )}
									   tooltip={errors.has(TransitionDialogFields.PAGERANGE) ? Translate.value(
										   errors.get(TransitionDialogFields.PAGERANGE)!, {ns: "message"}
									   ) : ""}
									   options={[
										   {label: Translate.value("inputs.pagerange.all"), value: PAGERANGE_ALL},
										   {
											   label: Translate.value("inputs.pagerange.current"),
											   value: this.state.currentPage
										   }
									   ]}/>
							{this.renderFieldError(errors.has(TransitionDialogFields.PAGERANGE) ?
								Translate.value(
									errors.get(TransitionDialogFields.PAGERANGE)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["transition-dialog", "large"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}
					appendTo={document.getElementById("root")} resizable={false}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.transition.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected onChange(field: TransitionDialogFields, value: any): void {
		let parameter: PortalPageTransitionsToolboxOptions =
			this.state.parameter!.data!.pageTransitions as PortalPageTransitionsToolboxOptions;
		let addParameter: AddPageTransitions = parameter.add![0] as AddPageTransitions;
		let errors: Map<TransitionDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case TransitionDialogFields.MODE:
				parameter.mode = value;
				break;
			case TransitionDialogFields.STYLE:
				addParameter.pageTransition!.style = value;
				break;
			case TransitionDialogFields.DIRECTION:
				addParameter.pageTransition!.direction = value;
				break;
			case TransitionDialogFields.DURATION:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = Transition.getDurationMin();
				}

				addParameter.pageTransition!.duration = DateHelper.unitToSeconds(value, TimeUnit.MILLISECOND);
				break;
			case TransitionDialogFields.AUTO_SCROLL:
				addParameter.pageTransition!.autoScroll = value;
				break;
			case TransitionDialogFields.SCROLL_TIME:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = Transition.getAutoScrollTimeMin();
				}

				addParameter.pageTransition!.autoScrollTime = DateHelper.unitToSeconds(value, TimeUnit.MILLISECOND);
				break;
			case TransitionDialogFields.FLY_OPAQUE:
				addParameter.pageTransition!.flyAreaOpaque = value;
				break;
			case TransitionDialogFields.FLY_SCALE:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = Transition.getFlyScaleDefault();
				}

				addParameter.pageTransition!.flyScale = value;
				break;
			case TransitionDialogFields.PAGERANGE:
				let checkPagerange: boolean = true;

				try {
					PageSelection.fromString(value, this.state.pages);
				} catch (e: any) {
					checkPagerange = false;
				}

				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!checkPagerange) {
					errors.set(field, "errors.fields.pagerange");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				addParameter.pageTransition!.pages = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-transition small"}/>
					<Translate {...this.props} data="transition.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canModify, Security.getCanModifyDefault()
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: PortalPageTransitionsToolboxOptions | undefined =
			this.state.parameter?.data?.pageTransitions as PortalPageTransitionsToolboxOptions;
		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let currentPage: string = documentSettings.activeViewer.currentPage.toString();
		let pageCount: number = await portalDocument.getNumberOfPages();

		if (!FunctionHelper.exists(parameter)) {
			return;
		}

		let hasTransition: boolean = FunctionHelper.applyValue(
			portalDocument.metadata?.transitions?.transition, TransitionsDocument.getTransitionDefault()
		).length > 0;
		if (!hasTransition) {
			parameter.mode = PageTransitionsFunction.ADD;
		}

		let addParameter: AddPageTransitions | undefined = parameter.add![0] as AddPageTransitions;

		addParameter.pageTransition!.pages = documentSettings.pageSelection.toString(
			pageCount, documentSettings.activeViewer.currentPage
		);

		this.setState({
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone(),
			pages: pageCount,
			currentPage: currentPage,
			hasTransition: hasTransition
		});
	};

	/**
	 * Handle the dialog submission
	 */
	protected async submit(): Promise<void> {
		let parameter: PortalPageTransitionsToolboxOptions | undefined =
			this.state.parameter?.data?.pageTransitions as PortalPageTransitionsToolboxOptions;
		let addParameter: AddPageTransitions = parameter.add![0] as AddPageTransitions;

		if (parameter.mode === PageTransitionsFunction.REMOVE) {
			parameter.remove!.push(RemovePageTransitions.fromJson({
				transitionSelection: {
					pages: addParameter.pageTransition?.pages
				}
			} as RemovePageTransitions));
		}

		this.setState({
			parameter: this.state.parameter
		}, async () => {
			await super.submit();

			let transitionOptionsParameter: PortalToolboxOptionsOptions = this.state.parameter!.data.clone();
			let transitionParameter: PortalPageTransitionsToolboxOptions =
				transitionOptionsParameter.pageTransitions as PortalPageTransitionsToolboxOptions;
			if (transitionParameter.mode === PageTransitionsFunction.REMOVE) {
				transitionParameter.add = undefined;
			} else {
				transitionParameter.remove = undefined;
			}

			let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
				options: transitionOptionsParameter.toJson()
			});

			let operationData: Array<BaseToolbox> = [toolboxOperation];
			let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
			let documentManager: PortalDocumentManager = this.props.context.documentManager;
			let selection: Array<PortalDocument> = documentManager.getSelection();

			for (let portalDocument of selection) {
				try {
					await this.props.context.session.executeRequest({
						webServiceType: webServiceType,
						document: portalDocument,
						operation: operationData,
						settings: Settings.fromJson({
							pdfa: {
								pdfaEditProfile: this.props.pdfaEditProfile
							},
							signature: {
								signatureEditProfile: this.props.signatureEditProfile
							},
							compress: {
								compressProfile: this.props.compressProfile,
								compressObjects: {
									objectStreamSize: this.props.objectStreamSize
								}
							}
						} as SettingsInterface)
					});
				} catch (exception: any) {
					if (await this.onError(exception)) {
						await this.submit();
					}
					return;
				}

				let message: Message = new Message("success.toolbox.transition", MessageType.SUCCESS);
				message.context = {filename: '"' + portalDocument.getFileName() + '" '};
				this.props.context.getPage().addMessage(message);
				this.hide();
			}
		});
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.TRANSITION;
	}

	protected getDefaultParameter(): Parameter<PortalToolboxOptionsOptions> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const TransitionDialog = React.memo(TransitionDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<PortalToolboxOptionsOptions>>,
		nextProps: Readonly<PortalDialogProperties<PortalToolboxOptionsOptions>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default TransitionDialog;